import React, { useState, useEffect } from "react";
import {
  Spinner,
  Form,
  Badge,
  Button,
  Pagination,
  Table,
} from "react-bootstrap";
import axios from "axios";

const BASE_URL = "https://dailydish.in/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

const OrderCard = ({ order, showDate }) => (
  <div
    style={{
      border: "1px solid #eee",
      borderRadius: 14,
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      overflow: "hidden",
      background: "#fff",
    }}
  >
    {/* Order header */}
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        background: "#f8faf9",
        borderBottom: "1px solid #eee",
      }}
    >
      <div>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#222" }}>
          {order.karleAdminId?.name || "—"}
        </div>
        <div style={{ fontSize: 12, color: "#888" }}>
          📱 {order.karleAdminId?.mobile || "—"}
          {showDate && (
            <span style={{ marginLeft: 10 }}>
              📅 {new Date(order.orderDate).toDateString()}
            </span>
          )}
        </div>
      </div>
      <Badge
        bg="none"
        style={{
          background: "#e8f4d4",
          color: "#3a5a0e",
          fontSize: 14,
          fontWeight: 800,
          padding: "8px 14px",
          borderRadius: 8,
        }}
      >
        ₹{order.totalAmount}
      </Badge>
    </div>

    {/* Items */}
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: 14 }}>
      {order.items.map((it, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            border: "1px solid #eee",
            borderRadius: 10,
            padding: "8px 12px",
            background: "#fcfcfc",
            minWidth: 220,
          }}
        >
          <img
            src={it.karleProductId?.foodImage}
            alt={it.foodName}
            style={{
              width: 46,
              height: 46,
              borderRadius: 8,
              objectFit: "cover",
              border: "1px solid #eee",
              background: "#f2f2f2",
              flexShrink: 0,
            }}
            onError={(e) => {
              e.target.style.visibility = "hidden";
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                color: "#333",
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={it.foodName}
            >
              {it.foodName}
            </div>
            <div style={{ fontSize: 13, marginTop: 2 }}>
              <span style={{ fontWeight: 800, color: "#1D9E75" }}>
                × {it.qty}
              </span>
              <span style={{ color: "#999", marginLeft: 8 }}>
                ₹{it.lineTotal}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const HistoryItemsCell = ({ items }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {items.map((it, idx) => (
      <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={it.karleProductId?.foodImage}
          alt={it.foodName}
          style={{
            width: 34,
            height: 34,
            borderRadius: 6,
            objectFit: "cover",
            border: "1px solid #eee",
            background: "#f2f2f2",
            flexShrink: 0,
          }}
          onError={(e) => {
            e.target.style.visibility = "hidden";
          }}
        />
        <div style={{ fontSize: 13 }}>
          <span style={{ color: "#333" }}>{it.foodName}</span>{" "}
          <span style={{ fontWeight: 800, color: "#1D9E75" }}>× {it.qty}</span>{" "}
          <span style={{ color: "#999" }}>₹{it.lineTotal}</span>
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ text }) => (
  <div
    style={{
      textAlign: "center",
      padding: "50px 20px",
      color: "#aaa",
      border: "1px dashed #ddd",
      borderRadius: 12,
    }}
  >
    <div style={{ fontSize: 40 }}>🧾</div>
    <p style={{ marginTop: 10 }}>{text}</p>
  </div>
);

const KarleSalesOrdersTab = () => {
  const [viewMode, setViewMode] = useState("date"); // 'date' | 'history'

  // Date-filtered view
  const [ordersDate, setOrdersDate] = useState(todayISO());
  const [orders, setOrders] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Order history (all dates, paginated, newest first)
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const HISTORY_LIMIT = 10;

  const getOrders = async (date) => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${BASE_URL}/karle/orders/all`, {
        params: { date },
      });
      if (res.data.success) {
        setOrders(res.data.data.orders);
        setGrandTotal(res.data.data.grandTotal);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getOrderHistory = async (page) => {
    try {
      setHistoryLoading(true);
      const res = await axios.get(`${BASE_URL}/karle/orders/history`, {
        params: { page, limit: HISTORY_LIMIT },
      });
      if (res.data.success) {
        setHistoryOrders(res.data.data.orders);
        setHistoryTotalPages(res.data.data.totalPages);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === "date") getOrders(ordersDate);
  }, [ordersDate, viewMode]);

  useEffect(() => {
    if (viewMode === "history") getOrderHistory(historyPage);
  }, [historyPage, viewMode]);

  const switchToHistory = () => {
    setViewMode("history");
    setHistoryPage(1);
  };

  const renderPagination = () => {
    if (historyTotalPages <= 1) return null;
    const items = [];
    let lastWasEllipsis = false;
    for (let p = 1; p <= historyTotalPages; p++) {
      if (
        p === 1 ||
        p === historyTotalPages ||
        (p >= historyPage - 1 && p <= historyPage + 1)
      ) {
        items.push(
          <Pagination.Item
            key={p}
            active={p === historyPage}
            onClick={() => setHistoryPage(p)}
          >
            {p}
          </Pagination.Item>,
        );
        lastWasEllipsis = false;
      } else if (!lastWasEllipsis) {
        items.push(<Pagination.Ellipsis key={`ellipsis-${p}`} disabled />);
        lastWasEllipsis = true;
      }
    }
    return (
      <Pagination className="justify-content-center mt-3">
        <Pagination.Prev
          disabled={historyPage === 1}
          onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
        />
        {items}
        <Pagination.Next
          disabled={historyPage === historyTotalPages}
          onClick={() =>
            setHistoryPage((p) => Math.min(historyTotalPages, p + 1))
          }
        />
      </Pagination>
    );
  };

  return (
    <div className="p-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 style={{ margin: 0 }}>Karle Sales Orders</h4>
          <p style={{ margin: 0, color: "#888", fontSize: "0.85rem" }}>
            {viewMode === "date"
              ? `${orders.length} order${orders.length !== 1 ? "s" : ""} on this date`
              : "All orders, newest to oldest"}
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          {viewMode === "date" ? (
            <>
              <Form.Control
                type="date"
                style={{ width: 200 }}
                value={ordersDate}
                onChange={(e) => setOrdersDate(e.target.value)}
              />
              <div
                style={{
                  background: "linear-gradient(135deg, #1D9E75, #17a37a)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 18px",
                  textAlign: "center",
                  minWidth: 150,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    opacity: 0.85,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Grand Total
                </div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>
                  ₹{grandTotal}
                </div>
              </div>
              <Button variant="outline-secondary" onClick={switchToHistory}>
                Order History
              </Button>
            </>
          ) : (
            <Button
              variant="outline-secondary"
              onClick={() => setViewMode("date")}
            >
              ← Back to Date View
            </Button>
          )}
        </div>
      </div>

      {viewMode === "date" ? (
        isLoading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="oranged" />
          </div>
        ) : orders.length === 0 ? (
          <EmptyState text="No orders for this date" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {orders.map((order) => (
              <OrderCard key={order._id} order={order} showDate={false} />
            ))}
          </div>
        )
      ) : historyLoading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="oranged" />
        </div>
      ) : historyOrders.length === 0 ? (
        <EmptyState text="No orders placed yet" />
      ) : (
        <>
          <Table
            responsive
            bordered
            style={{ width: "-webkit-fill-available" }}
          >
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Date</th>
                <th>Karle Admin</th>
                <th>Mobile</th>
                <th>Items</th>
                <th>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {historyOrders.map((order, idx) => (
                <tr key={order._id}>
                  <td>{(historyPage - 1) * HISTORY_LIMIT + idx + 1}</td>
                  <td>{new Date(order.orderDate).toDateString()}</td>
                  <td>{order.karleAdminId?.name || "—"}</td>
                  <td>{order.karleAdminId?.mobile || "—"}</td>
                  <td>
                    <HistoryItemsCell items={order.items} />
                  </td>
                  <td style={{ fontWeight: 800, color: "#3a5a0e" }}>
                    ₹{order.totalAmount}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default KarleSalesOrdersTab;
